// V4 — Mobile-first Schedule view. Same data + write paths as the
// desktop Schedules view, re-styled as a card list:
//
//   • Sport-filter chip strip
//   • One bordered card per match, with day-strip header,
//     home/vs/opponent block, time + venue chips, status tag, and a
//     row of action icons for elevated users.
//   • "Add Match" button promotes to a sticky pill above the list
//     for admins. The form modal is reused as-is from the desktop
//     view via a render-prop, since refactoring it is out of scope.

import React, { useMemo, useState } from "react";
import {
  Plus, Pencil, Trash2, Filter, CheckCircle2, ClipboardList,
  ScrollText, CalendarDays, Clock, MapPin, Tag
} from "lucide-react";
import { useActiveTeam } from "../components/ActiveTeam";
import { useToast } from "../components/Toast";
import MatchResultEntry from "../components/MatchResultEntry";
import { fmtMatchTime } from "../utils/time";
import { api } from "../api";
import { canDo } from "../data";

const SPORT_FILTERS = ["All", "Cricket", "Volleyball"];

function dayBadge(iso) {
  if (!iso) return "TBD";
  const today = new Date(); today.setHours(0,0,0,0);
  const d = new Date(`${iso}T00:00:00`);
  const diff = Math.round((d - today) / (1000 * 60 * 60 * 24));
  if (diff === 0) return "TODAY";
  if (diff === 1) return "TOMORROW";
  if (diff === -1) return "YESTERDAY";
  return d.toLocaleDateString("en-US", { weekday: "short" }).toUpperCase();
}
function dateLong(iso) {
  if (!iso) return "—";
  return new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export default function ScheduleMobile({
  state, reload, user, navigate,
  openCreate, openEdit   // bound from desktop Schedules so the form modal still works
}) {
  const toast = useToast();
  const { activeTeam, activeTeamId, isAllTeams } = useActiveTeam();
  const [sportFilter, setSportFilter] = useState("All");
  const [resultFor, setResultFor] = useState(null);

  const canCreate = canDo(user, "schedule", "create");
  const canUpdate = canDo(user, "schedule", "update");
  const canDelete = canDo(user, "schedule", "delete");
  const canRecordResult = canDo(user, "matchPerformance", "update");
  const today = new Date().toISOString().slice(0, 10);

  const matches = useMemo(() => {
    let pool = state.matches;
    if (!isAllTeams) pool = pool.filter(m => m.teamId === activeTeamId);
    if (sportFilter !== "All") pool = pool.filter(m => m.sport === sportFilter);
    return [...pool].sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time));
  }, [state.matches, sportFilter, isAllTeams, activeTeamId]);

  const upcoming = matches.filter(m => m.date >= today);
  const past     = matches.filter(m => m.date < today).reverse();

  // When viewing "All Teams", group matches under per-team headers
  // within each section so the user can scan team-by-team.
  function groupByTeam(arr) {
    const out = new Map();
    for (const m of arr) {
      if (!out.has(m.teamId)) out.set(m.teamId, []);
      out.get(m.teamId).push(m);
    }
    return out;
  }
  const upcomingByTeam = useMemo(() => groupByTeam(upcoming), [upcoming]);
  const pastByTeam     = useMemo(() => groupByTeam(past), [past]);
  const teamOrder = useMemo(() => {
    return [...state.teams]
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
      .map(t => t.id);
  }, [state.teams]);

  async function remove(m) {
    const teamName = state.teams.find(t => t.id === m.teamId)?.name || "Pitt Gabruz";
    if (!confirm(`Delete this match (${teamName} vs ${m.opponent} on ${m.date})?`)) return;
    try {
      await api.deleteMatch(m.id);
      await reload();
      toast.success("Match deleted.");
    } catch (err) { toast.error("Delete failed: " + err.message); }
  }

  return (
    <div className="m-sched">
      {/* Header strip */}
      <header className="m-sched-head">
        <div>
          <small>{isAllTeams ? "All Teams" : (activeTeam?.name || "Pitt Gabruz")}</small>
          <h1>Schedule</h1>
        </div>
        {canCreate && (
          <button type="button" className="btn m-sched-addBtn" onClick={openCreate}>
            <Plus size={16}/> Add
          </button>
        )}
      </header>

      {/* Sport filter chips */}
      <div className="m-sched-filterRow">
        <Filter size={14} style={{color:"var(--gold)"}}/>
        {SPORT_FILTERS.map(s => (
          <button
            key={s}
            type="button"
            className={sportFilter === s ? "m-sched-filterChip m-sched-filterChip--active" : "m-sched-filterChip"}
            onClick={() => setSportFilter(s)}
          >{s}</button>
        ))}
        <small style={{marginLeft:"auto"}}>{matches.length}</small>
      </div>

      {/* Upcoming matches — primary section, always at the top */}
      {upcoming.length > 0 && (
        <section className="m-sched-section">
          <h3 className="m-sched-h3">Upcoming · {upcoming.length}</h3>
          {isAllTeams ? (
            teamOrder
              .filter(tid => upcomingByTeam.has(tid))
              .map(tid => {
                const team = state.teams.find(t => t.id === tid);
                const rows = upcomingByTeam.get(tid) || [];
                return (
                  <div key={tid} className="m-sched-teamGroup">
                    <div className="m-sched-teamHead">
                      <strong>{team?.name || `Team ${tid}`}</strong>
                      <em>{rows.length}</em>
                    </div>
                    <div className="m-sched-list">
                      {rows.map(m => (
                        <MatchCard key={m.id} m={m} state={state}
                          onSquad={() => navigate?.("squad")}
                          onAvailability={() => navigate?.("availability")}
                          onEdit={canUpdate ? () => openEdit(m) : null}
                          onDelete={canDelete ? () => remove(m) : null}
                          onResult={canRecordResult && m.sport === "Cricket" && m.date <= today ? () => setResultFor(m) : null}
                          isPast={false}/>
                      ))}
                    </div>
                  </div>
                );
              })
          ) : (
            <div className="m-sched-list">
              {upcoming.map(m => (
                <MatchCard key={m.id} m={m} state={state}
                  onSquad={() => navigate?.("squad")}
                  onAvailability={() => navigate?.("availability")}
                  onEdit={canUpdate ? () => openEdit(m) : null}
                  onDelete={canDelete ? () => remove(m) : null}
                  onResult={canRecordResult && m.sport === "Cricket" && m.date <= today ? () => setResultFor(m) : null}
                  isPast={false}/>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Past matches — secondary section below upcoming */}
      {past.length > 0 && (
        <section className="m-sched-section">
          <h3 className="m-sched-h3 m-sched-h3--muted">Past · {past.length}</h3>
          {isAllTeams ? (
            teamOrder
              .filter(tid => pastByTeam.has(tid))
              .map(tid => {
                const team = state.teams.find(t => t.id === tid);
                const rows = pastByTeam.get(tid) || [];
                return (
                  <div key={tid} className="m-sched-teamGroup">
                    <div className="m-sched-teamHead">
                      <strong>{team?.name || `Team ${tid}`}</strong>
                      <em>{rows.length}</em>
                    </div>
                    <div className="m-sched-list">
                      {rows.map(m => (
                        <MatchCard key={m.id} m={m} state={state}
                          onSquad={() => navigate?.("squad")}
                          onAvailability={null}
                          onEdit={canUpdate ? () => openEdit(m) : null}
                          onDelete={canDelete ? () => remove(m) : null}
                          onResult={canRecordResult && m.sport === "Cricket" ? () => setResultFor(m) : null}
                          isPast={true}/>
                      ))}
                    </div>
                  </div>
                );
              })
          ) : (
            <div className="m-sched-list">
              {past.map(m => (
                <MatchCard key={m.id} m={m} state={state}
                  onSquad={() => navigate?.("squad")}
                  onAvailability={null}
                  onEdit={canUpdate ? () => openEdit(m) : null}
                  onDelete={canDelete ? () => remove(m) : null}
                  onResult={canRecordResult && m.sport === "Cricket" ? () => setResultFor(m) : null}
                  isPast={true}/>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Empty state */}
      {matches.length === 0 && (
        <div className="m-sched-empty">
          <CalendarDays size={32}/>
          <p>No matches{sportFilter !== "All" ? ` for ${sportFilter}` : ""}.</p>
          {canCreate && (
            <button type="button" className="btn" onClick={openCreate}>
              <Plus size={14}/> Add a match
            </button>
          )}
        </div>
      )}

      {resultFor && (
        <MatchResultEntry
          match={resultFor}
          team={state.teams.find(t => t.id === resultFor.teamId)}
          squad={state.squads.find(s => s.matchId === resultFor.id)}
          members={state.members}
          user={user}
          onClose={() => setResultFor(null)}
          onSaved={() => reload && reload()}
        />
      )}
    </div>
  );
}

function MatchCard({ m, state, onSquad, onAvailability, onEdit, onDelete, onResult, isPast }) {
  const team = state.teams.find(t => t.id === m.teamId);
  const homeShort = (team?.name || "Pitt Gabruz")
    .replace(/(Cricket|Volleyball|Team|Club)/gi, "")
    .trim() || "Pitt Gabruz";
  // Quick RSVP count for the in-card hint (admins / captains scan this)
  const counts = useMemo(() => {
    const rows = state.availability.filter(a => a.matchId === m.id);
    return {
      a: rows.filter(r => r.status === "Available").length,
      t: rows.filter(r => r.status === "Tentative").length,
      n: rows.filter(r => r.status === "Not Available").length
    };
  }, [m.id, state.availability]);

  return (
    <article className={`m-sched-card${isPast ? " m-sched-card--past" : ""}`}>
      <div className="m-sched-card-dayStrip">
        <span className="m-sched-card-day">{dayBadge(m.date)}</span>
        <span className="m-sched-card-date">{dateLong(m.date)}</span>
        <span className="m-sched-card-time"><Clock size={11}/> {m.time ? `${fmtMatchTime(m.time)} ET` : "TBD"}</span>
        <span className="m-sched-card-sport">{m.sport}</span>
      </div>

      <div className="m-sched-card-vs">
        <div className="m-sched-card-team">
          <small>HOME</small>
          <b>{homeShort.toUpperCase()}</b>
        </div>
        <span className="m-sched-card-vsLabel">VS</span>
        <div className="m-sched-card-team">
          <small>AWAY</small>
          <b>{m.opponent.toUpperCase()}</b>
        </div>
      </div>

      <div className="m-sched-card-meta">
        {m.venue && (
          <span className="m-sched-card-chip">
            <MapPin size={11}/> {m.venue}
          </span>
        )}
        <span className="m-sched-card-chip">
          <Tag size={11}/> {m.type || "League Match"}
        </span>
        <span className={`m-sched-card-status m-sched-card-status--${(m.status || "scheduled").toLowerCase()}`}>
          {m.status || "Scheduled"}
        </span>
      </div>

      {!isPast && (counts.a + counts.t + counts.n > 0) && (
        <div className="m-sched-card-rsvp">
          <span style={{color:"var(--green)"}}>{counts.a} Yes</span>
          <span style={{color:"var(--orange)"}}>{counts.t} Maybe</span>
          <span style={{color:"var(--red)"}}>{counts.n} No</span>
        </div>
      )}

      <div className="m-sched-card-actions">
        {onAvailability && (
          <button type="button" className="dark" onClick={onAvailability}>
            <CheckCircle2 size={13}/> RSVP
          </button>
        )}
        <button type="button" className="dark" onClick={onSquad}>
          <ClipboardList size={13}/> Squad
        </button>
        {onResult && (
          <button type="button" className="dark m-sched-card-resultBtn" onClick={onResult}>
            <ScrollText size={13}/> Result
          </button>
        )}
        {onEdit && (
          <button
            type="button"
            className="dark m-sched-editBtn"
            onClick={onEdit}
            title="Edit schedule details"
          >
            <Pencil size={13}/> Edit
          </button>
        )}
        {onDelete && (
          <button
            type="button"
            className="dark"
            onClick={onDelete}
            title="Delete"
            style={{color:"var(--red)", borderColor:"rgba(255,107,107,0.4)"}}
          ><Trash2 size={13}/></button>
        )}
      </div>
    </article>
  );
}
